import { forwardRef, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';

import { Box } from '@sternum/styled-system';
import { Popup, PopupProps } from '@sternum/ui';

import { Icon } from '../Icon';

import { Container, Content, Header } from './DraggablePopup.styles';

export interface DraggablePopupProps extends Pick<PopupProps, 'offsetX' | 'offsetY' | 'show' | 'trackedElementRef'> {
  children: ReactNode;
  width?: number;
  onClose?: () => void;
  defaultPosition?: { x: number; y: number };
  positionDirection?: PopupProps['position'];
}

export const DraggablePopup = forwardRef<HTMLDivElement, DraggablePopupProps>(
  ({ children, width = 400, onClose, defaultPosition, positionDirection = 'bottom', ...popupProps }, ref) => {
    const [isDrag, setIsDrag] = useState(false);
    const draggableRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const requestRef = useRef<number>();

    const initiallySetPosition = useCallback(() => {
      if (popupProps.show) {
        const { x, y } = draggableRef.current?.getBoundingClientRect() || ({ x: 0, y: 0 } as DOMRect);

        setPosition(defaultPosition || { x, y });
      } else {
        setPosition({ x: 0, y: 0 });
      }
    }, [defaultPosition, popupProps.show]);

    useEffect(() => {
      if (defaultPosition) return;

      initiallySetPosition();
    }, [defaultPosition, initiallySetPosition]);

    useLayoutEffect(() => {
      if (!defaultPosition) return;

      initiallySetPosition();
    }, [defaultPosition, initiallySetPosition]);

    const handleDrag = (_e: DraggableEvent, data: DraggableData) => {
      requestRef.current = requestAnimationFrame(() => {
        const { x, y } = data;

        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;

        const elementWidth = draggableRef.current?.offsetWidth || 0;
        const elementHeight = draggableRef.current?.offsetHeight || 0;

        const maxX = screenWidth - elementWidth;
        const maxY = screenHeight - elementHeight;

        // Update the position within the screen boundaries
        setPosition({
          x: Math.min(Math.max(x, 0), maxX),
          y: Math.min(Math.max(y, 0), maxY),
        });
      });
    };

    useEffect(() => {
      return () => {
        if (requestRef.current) {
          cancelAnimationFrame(requestRef.current);
        }
      };
    }, []);

    return (
      <Draggable disabled={!isDrag} defaultPosition={defaultPosition} position={position} onDrag={handleDrag}>
        <Popup ref={ref} position={positionDirection} {...popupProps}>
          <Container ref={draggableRef} minWidth={width} maxWidth={width}>
            <Header>
              <Box
                cursor="grab"
                display="inline"
                onMouseEnter={() => setIsDrag(true)}
                onMouseLeave={() => setIsDrag(false)}
                padding="4px"
              >
                <Icon
                  type="SixDotsVerticalIcon"
                  size={12}
                  color={{ Dark: 'GreyDarkerMiddle', Light: 'GreyDarkerMiddle' }}
                />
              </Box>
              <Box onClick={onClose} cursor="pointer" padding="4px">
                <Icon type="XIcon" size={18} />
              </Box>
            </Header>
            <Content>{children}</Content>
          </Container>
        </Popup>
      </Draggable>
    );
  },
);

DraggablePopup.displayName = 'DraggablePopup';
